import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import * as React from "react";
// import LocationOnIcon from '@mui/icons-material/LocationOn'
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import { debounce } from "@mui/material/utils";
import { googleAPIKey } from "../../firebase-config/google";
// import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined'
import { InputBase } from "@mui/material";
const parse = require("autosuggest-highlight/parse");

const autocompleteService = { current: null };

function loadScript(src: string, position: HTMLElement | null, id: string) {
  if (!position) {
    return;
  }

  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
}

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}
interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}
interface PlaceType {
  description: string;
  structured_formatting: StructuredFormatting;
}

export default function AddressAutoComplete({
  onSelect,
  customInput,
  defaultValue,
  minWidth = 200,
  omitAddressFilter = false,
  CustomInput = null,
  key = "autocompletedefaultkey",
  inputBaseWidth = "100%",
}: {
  defaultValue?: PlaceType;
  onSelect: any;
  customInput?: boolean;
  minWidth?: number;
  omitAddressFilter?: boolean;
  CustomInput?: any;
  key?: any;
  inputBaseWidth?: any;
}) {
  console.log("key autocomplete", key);
  const [value, setValue] = React.useState<PlaceType | null>(null);
  const [inputValue, setInputValue] = React.useState<any>("");
  const [options, setOptions] = React.useState<readonly PlaceType[]>([]);

  const LocationIcon = require("assets/img/png/location_ic.png");

  const loaded = React.useRef(false);

  if (typeof window !== "undefined" && !loaded.current) {
    if (!document.querySelector("#google-maps")) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${googleAPIKey}&libraries=places`,
        document.querySelector("head"),
        "google-maps"
      );
    }

    loaded.current = true;
  }

  React.useEffect(() => {
    if (!defaultValue) return;
    setInputValue(defaultValue?.structured_formatting?.main_text ?? "");
  }, [defaultValue]);

  const fetch = React.useMemo(
    () =>
      debounce(
        (
          request: {
            input: string;
            componentRestrictions: { country: "us" };
            fields: ["address_components", "geometry"];
            types: ["address"];
          },
          callback: () => void
        ) => {
          (autocompleteService.current as any).getPlacePredictions(
            request,
            callback
          );
        },
        400
      ),
    []
  );

  React.useEffect(() => {
    let active = true;

    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (
        window as any
      ).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    const mapRequestFilters: any = {
      input: inputValue,
      componentRestrictions: { country: "us" },
      fields: ["address_components", "geometry"],
      ...(!omitAddressFilter ? { types: ["address"] } : {}),
    };

    fetch(mapRequestFilters, (results?: readonly PlaceType[]) => {
      if (active) {
        let newOptions: readonly PlaceType[] = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch, omitAddressFilter]);

  return (
    <Autocomplete
      id="google-map-demo"
      key={key}
      sx={{ minWidth: minWidth }}
      getOptionLabel={(option) =>
        typeof option === "string"
          ? option
          : option?.structured_formatting?.main_text
      }
      filterOptions={(x) => x}
      options={options}
      fullWidth
      autoComplete
      includeInputInList
      filterSelectedOptions
      defaultValue={defaultValue ?? value}
      inputValue={
        typeof inputValue === "string"
          ? inputValue
          : inputValue?.structured_formatting?.main_text
      }
      noOptionsText="No locations"
      onChange={(event: any, newValue: PlaceType | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        onSelect(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        console.log(newInputValue, "newInputValue");
        if (newInputValue !== "undefined") {
          if (!newInputValue) {
            onSelect("");
          }
          setInputValue(newInputValue);
        }
      }}
      renderInput={(params) =>
        CustomInput ? (
          CustomInput(params)
        ) : customInput ? (
          <Box
            display="flex"
            alignItems="center"
            sx={{ position: "relative", width: "100%" }}
          >
            <InputBase
              ref={params.InputProps.ref}
              sx={{ width: inputBaseWidth }}
              inputProps={{
                ...params.inputProps,
                style: { minWidth: minWidth, width: inputBaseWidth },
              }}
              placeholder={"Search Location"}
            />
            {/* <LocationOnOutlined /> */}
            <img
              src={LocationIcon}
              style={{ position: "absolute", right: 0, bottom: 5 }}
            />
          </Box>
        ) : (
          <TextField
            {...params}
            inputProps={{
              ...params.inputProps,
              style: { height: 9 },
            }}
            placeholder="Street address or P.O Box"
            fullWidth
            sx={{ button: { display: "none" } }}
          />
        )
      }
      renderOption={(props, option) => {
        const matches =
          option.structured_formatting.main_text_matched_substrings || [];

        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match: any) => [
            match.offset,
            match.offset + match.length,
          ])
        );

        return (
          <li {...props}>
            <Grid container alignItems="center">
              <Grid item sx={{ display: "flex", width: 44 }}>
                {/* <LocationOnIcon sx={{ color: 'text.secondary' }} /> */}
                <img src={LocationIcon} />
              </Grid>
              <Grid
                item
                sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}
              >
                {parts.map((part: any, index: number) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                  >
                    {part.text}
                  </Box>
                ))}
                <Typography variant="body2" color="text.secondary">
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}
